import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { decodeData } from '../../../utils/encodeAndEncode';
import { Icon } from '@iconify/react';
import { getTraineeAnalytics } from '../../../services/traineeService';
import { toast } from 'react-toastify';
import Loader from '../../../components/common/Loader';
import AdminPage from '../../../components/admin/AdminPage';
import TraineeAnalyticsCourseTab from './TraineeAnalyticsCourseTab';
import TraineeAnalyticsClassroomTab from './TraineeAnalyticsClassroomTab';
import TraineeAnalyticsTicketsTab from './TraineeAnalyticsTicketsTab';
// import TraineeAnalyticsPaymentsTab from './TraineeAnalyticsPaymentsTab';
import TraineeAnalyticsCertificatesTab from './TraineeAnalyticsCertificatesTab';
import TraineeAnalyticsGamification from './TraineeAnalyticsGamification';

function TraineeAnalytics() {
  const { traineeId } = useParams();
  const [activeTab, setActiveTab] = useState('courses');
  let decodedId;
  
  try {
    decodedId = decodeData(traineeId);
  } catch (error) {
    console.error('Error decoding trainee ID:', error);
    decodedId = 'Invalid ID';
  }

  const [loading, setLoading] = useState(false);
  const [traineeData, setTraineeData] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({
    active_classrooms: 0,
    active_courses: 0,
    free_courses: 0,
    paid_courses: 0,
    completed_courses: 0,
    certificates: 0,
    pending_tickets: 0,
    resolved_tickets: 0
  });

  // Add state for tab data
  const [tabData, setTabData] = useState({
    courses: [],
    classrooms: [],
    tickets: [],
    payments: [],
    certificates: []
  });

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const response = await getTraineeAnalytics({trainee_id: decodedId});
      // console.log('Trainee Analytics Data:', response);

      if (response.success) {
        setTraineeData(response.data.trainee);
        setDashboardStats(response.data.dashboard);
        // Set tab data from response
        setTabData({
          courses: response.data.courses || [],
          classrooms: response.data.classrooms || [],
          tickets: response.data.tickets || [],
          payments: response.data.payments || [],
          certificates: response.data.certificates || []
        });
      } else {
        toast.error(response.message || 'Failed to fetch analytics data');
      }
    } catch (error) {
        console.error('Error fetching trainee analytics:', error);
        toast.error('Something went wrong while fetching analytics data');
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    if (decodedId) {
      fetchAnalyticsData();
    }
  }, [decodedId]);

  const tabs = [
    {
      id: 'courses',
      label: 'Courses',
      icon: 'material-symbols:book-outline'
    },
    {
      id: 'classroom',
      label: 'Live Sessions',
      icon: 'material-symbols:grid-view'
    },
    {
      id: 'tickets',
      label: 'Tickets',
      icon: 'material-symbols:confirmation-number-outline'
    },
    // {
    //   id: 'payments',
    //   label: 'Payments',
    //   icon: 'material-symbols:payments-outline'
    // },
    {
      id: 'certificates',
      label: 'Certificates',
      icon: 'material-symbols:verified-outline'
    },
    {
      id: 'gamification',
      label: 'Gamification',
      icon: 'fluent:trophy-24-regular'
    }
  ];

  const getDashboardCards = () => {
    return [
      {
        id: 1,
        title: "Active Live Sessions",
        value: dashboardStats.active_classrooms || 0,
        icon: "material-symbols:grid-view",
        color: "success"
      },
      {
        id: 2,
        title: "Active Courses",
        value: dashboardStats.active_courses || 0,
        icon: "material-symbols:book-outline",
        color: "success"
      },
      {
        id: 3,
        title: "Free Courses",
        value: dashboardStats.free_courses || 0,
        icon: "material-symbols:star-outline",
        color: "warning"
      },
      // {
      //   id: 4,
      //   title: "Paid Courses",
      //   value: dashboardStats.paid_courses || 0,
      //   icon: "material-symbols:credit-card",
      //   color: "info"
      // },
      {
        id: 5,
        title: "Completed Courses",
        value: dashboardStats.completed_courses || 0,
        icon: "material-symbols:check-circle-outline",
        color: "danger"
      },
      {
        id: 6,
        title: "Certificates",
        value: dashboardStats.certificates || 0,
        icon: "material-symbols:verified-outline",
        color: "success"
      },
      {
        id: 7,
        title: "Pending Tickets",
        value: dashboardStats.pending_tickets || 0,
        icon: "material-symbols:support-agent",
        color: "warning"
      },
      {
        id: 8,
        title: "Resolved Tickets",
        value: dashboardStats.resolved_tickets || 0,
        icon: "material-symbols:check-circle",
        color: "success"
      }
    ];
  };

  const dashboardCards = getDashboardCards();

  const StatCard = ({ title, value, icon, color }) => {
    const colorMap = {
      success: {
        wrap: 'bg-emerald-50 text-emerald-700',
        icon: 'text-emerald-700',
      },
      warning: {
        wrap: 'bg-amber-50 text-amber-700',
        icon: 'text-amber-700',
      },
      info: {
        wrap: 'bg-blue-50 text-blue-700',
        icon: 'text-blue-700',
      },
      danger: {
        wrap: 'bg-rose-50 text-rose-700',
        icon: 'text-rose-700',
      },
    };
    const theme = colorMap[color] || colorMap.info;
    return (
      <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-gray-500 text-xs font-semibold tracking-wide uppercase mb-1 truncate">{title}</p>
            <p className="text-xl font-bold text-gray-900 leading-none">{value}</p>
          </div>
          <div className={`inline-flex items-center justify-center rounded-2xl p-2.5 ${theme.wrap}`}>
            <Icon icon={icon} width="20" height="20" className={theme.icon} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <AdminPage title="Trainee Analytics" description="Loading...">
        <div className="flex justify-center items-center min-h-[360px] rounded-2xl bg-gray-50/50 border border-gray-100">
          <Loader />
        </div>
      </AdminPage>
    );
  }

  if (!decodedId || decodedId === 'Invalid ID') {
    return (
      <AdminPage title="Trainee Analytics">
        <div className="rounded-2xl border border-red-100 bg-red-50/50 p-8 text-center">
          <Icon icon="fluent:alert-circle-24-regular" className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-red-700 font-medium">Invalid trainee ID.</p>
          <Link to="/admin/trainees" className="inline-flex items-center gap-2 mt-4 px-4 py-2.5 rounded-xl bg-red-100 text-red-700 font-medium hover:bg-red-200 no-underline">
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back to Trainees
          </Link>
        </div>
      </AdminPage>
    );
  }

  // Render tab content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case 'courses':
        return <TraineeAnalyticsCourseTab traineeId={decodedId} />;
      case 'classroom':
        return <TraineeAnalyticsClassroomTab traineeId={decodedId} />;
      case 'tickets':
        return <TraineeAnalyticsTicketsTab traineeId={decodedId} />;
      // case 'payments':
      //   return <TraineeAnalyticsPaymentsTab traineeId={decodedId} />;
      case 'certificates':
        return <TraineeAnalyticsCertificatesTab traineeId={decodedId} />;
      case 'gamification':
        return <TraineeAnalyticsGamification traineeId={decodedId} />;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Available';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <AdminPage
      title="Trainee Analytics"
      description={traineeData ? `${traineeData.name} – courses, live sessions, and activity` : 'View trainee details and analytics.'}
      back={
        <Link
          to="/admin/trainees"
          className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 no-underline"
          aria-label="Back to Trainees"
          title="Back to Trainees"
        >
          <Icon icon="fluent:arrow-left-24-regular" width="20" height="20" />
        </Link>
      }
      actions={
        <Link
          to="/admin/trainees"
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 no-underline"
        >
          <Icon icon="fluent:people-24-regular" width="18" height="18" />
          All Trainees
        </Link>
      }
    >
      {/* Trainee Information Card */}
      {traineeData && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mb-6 overflow-hidden">
          <div className="p-5 sm:p-6">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative shrink-0">
                  {traineeData.profile_pic_url ? (
                    <img
                      src={traineeData.profile_pic_url}
                      alt={traineeData.name || 'Trainee profile'}
                      className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border border-gray-100 shadow-sm"
                    />
                  ) : (
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center bg-primary text-white text-2xl sm:text-3xl font-semibold border border-gray-100 shadow-sm">
                      {traineeData.name?.charAt(0).toUpperCase() || 'T'}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">{traineeData.name}</h2>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold bg-gray-50 border border-gray-200 text-gray-700">
                      <Icon icon="fluent:person-24-regular" width="14" height="14" />
                      ID: {traineeData.id}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5 min-w-0">
                      <Icon icon="fluent:mail-24-regular" width="16" height="16" className="text-gray-400" />
                      <span className="truncate max-w-[360px]">{traineeData.email}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Icon icon="fluent:phone-24-regular" width="16" height="16" className="text-gray-400" />
                      <span>{traineeData.mobileno_code} {traineeData.mobile}</span>
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Personal</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">DOB</span>
                        <span className="font-medium text-gray-900">{formatDate(traineeData.date_of_birth)}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Gender</span>
                        <span className="font-medium text-gray-900 capitalize">{traineeData.gender || 'Not Specified'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50/50 p-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Professional</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Qualification</span>
                        <span className="font-medium text-gray-900 truncate">{traineeData.qualification || 'Not Specified'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Profession</span>
                        <span className="font-medium text-gray-900 truncate">{traineeData.profession || 'Not Specified'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-gray-500">Location</span>
                        <span className="font-medium text-gray-900 truncate">{traineeData.state}, {traineeData.country} - {traineeData.zipcode}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-3 mb-6">
        {dashboardCards.map((item) => (
          <StatCard key={item.id} title={item.title} value={item.value} icon={item.icon} color={item.color} />
        ))}
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white sticky top-0 z-10">
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex px-3 pt-3 pb-0 gap-2 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`flex items-center gap-2.5 px-5 py-3.5 rounded-t-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-white text-primary shadow-sm border-t-2 border-x-2 border-primary border-b-0 -mb-[1px] relative z-10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <Icon icon={tab.icon} width="20" height="20" className={activeTab === tab.id ? 'text-primary' : ''} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-5">
          {renderTabContent()}
        </div>
      </div>
    </AdminPage>
  );
}

export default TraineeAnalytics;
